// TODO Delete the entire render-fields-analytics-old folder when cleaning gic-remove-unused-fieldstorender-events_o3mhl
import { useCallback, useEffect, useMemo, useRef } from 'react';
import isEqual from 'lodash/isEqual';
import type { FieldTabs } from '@atlassian/jira-issue-create-common-types';
import { useTriggerIssueCreateModal } from '@atlassian/jira-packages-controllers-use-trigger-issue-create-modal';
import {
	fireTrackAnalyticsDeferred,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';
import { globalIssueCreateTTIWithAdjustments } from '@atlassian/jira-ui-modifications-analytics';

export const useRenderedFields = (fieldTabs: FieldTabs) => {
	const [{ isModalOpen }] = useTriggerIssueCreateModal();

	const fieldsRendered = useRef<string[][]>([]);

	const fieldUnmounted = useRef(false);

	const allFields = useMemo(() => fieldTabs.flatMap((tab) => tab.items), [fieldTabs]);

	const { createAnalyticsEvent } = useAnalyticsEvents();

	useEffect(() => {
		const analyticsEvent = createAnalyticsEvent({});
		fireTrackAnalyticsDeferred(analyticsEvent, 'gic fieldsToRender', {
			fields: allFields,
		});
	}, [allFields, createAnalyticsEvent]);

	const checkIfAllFieldsRendered = useCallback(() => {
		const flattenedFields = fieldsRendered.current.flat();
		if (isEqual(flattenedFields.sort(), allFields.sort())) {
			fieldUnmounted.current = false;
			const analyticsEvent = createAnalyticsEvent({});
			fireTrackAnalyticsDeferred(analyticsEvent, 'gic fieldsRendered', {
				fields: allFields,
			});

			// The last call to `mark` is the time used in the final metric
			// We use this fact to obtain when the final field was mounted
			globalIssueCreateTTIWithAdjustments.mark('gic-all-fields-rendered');
		}
	}, [allFields, createAnalyticsEvent]);

	const onFieldRendered = useCallback(
		(fieldId: string) => {
			const mountedField = [fieldId];
			fieldsRendered.current.push(mountedField);
			checkIfAllFieldsRendered();

			return () => {
				fieldsRendered.current = fieldsRendered.current.filter(
					(currentField) => mountedField !== currentField,
				);

				if (!fieldUnmounted.current && isModalOpen) {
					fieldUnmounted.current = true;
					const analyticsEvent = createAnalyticsEvent({});
					fireTrackAnalyticsDeferred(analyticsEvent, 'gic fieldUnmounted', {
						field: fieldId,
					});
				}
			};
		},
		[checkIfAllFieldsRendered, createAnalyticsEvent, isModalOpen],
	);

	return {
		onFieldRendered,
	};
};

type MarkFieldAsRenderedProps = {
	fieldId: string;
	onFieldRendered: ReturnType<typeof useRenderedFields>['onFieldRendered'];
};

export const MarkFieldAsRendered: React.FC<MarkFieldAsRenderedProps> = ({
	onFieldRendered,
	fieldId,
}) => {
	useEffect(() => {
		const unmount = onFieldRendered(fieldId);
		return unmount;
	}, [onFieldRendered, fieldId]);

	return null;
};
